import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { PlatformIcon } from './shared/PlatformIcon.jsx';
import {
  PLATFORMS,
  PLATFORM_LABELS,
  FORMAT_OPTIONS,
  GOAL_OPTIONS,
  TONE_OPTIONS,
  VISUAL_STYLE_OPTIONS,
} from './shared/platformLabels.js';
import { fromPublishingForm } from './shared/brandFormUtils.js';
import { api } from '../../services/api/client.js';

const DAY_OPTIONS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function PublishingRulesTab({ publishingForm, setPublishingForm, platformPrefs, setPlatformPrefs }) {
  const { t } = useTranslation();
  const [savingGlobal, setSavingGlobal] = useState(false);

  const update = (key, value) => setPublishingForm((prev) => ({ ...prev, [key]: value }));
  const days = useMemo(
    () => String(publishingForm.preferredDaysText || '').split(',').map((d) => d.trim().toLowerCase()).filter(Boolean),
    [publishingForm.preferredDaysText],
  );
  const toggleDay = (day) => {
    const next = days.includes(day) ? days.filter((d) => d !== day) : [...days, day];
    update('preferredDaysText', next.join(', '));
  };

  async function saveGlobal() {
    try {
      setSavingGlobal(true);
      const result = await api.profile.updatePublishingPreferences(fromPublishingForm(publishingForm));
      const next = result.publishingPreferences;
      setPublishingForm({
        autopilotMode: next.autopilotMode,
        postsPerWeek: next.postsPerWeek,
        preferredDaysText: (next.preferredDays || []).join(', '),
        preferredTimesText: (next.preferredTimes || []).join(', '),
        timezone: next.timezone,
        requireApprovalForSalesContent: next.requireApprovalForSalesContent,
        requireApprovalForNewPlatforms: next.requireApprovalForNewPlatforms,
      });
      notify('publishing.saved');
    } catch (err) {
      notify('publishing.saveFailed', { error: err });
    } finally {
      setSavingGlobal(false);
    }
  }

  return (
    <>
      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">{t('publishingRulesTab.globalPreferencesTitle')}</h3>
          <p className="settings-card-description">{t('publishingRulesTab.globalPreferencesDescription')}</p>
        </div>

        <div className="profile-form-grid">
          <Field label={t('publishingRulesTab.timezoneLabel')}>
            <input className="input form-input" value={publishingForm.timezone} onChange={(e) => update('timezone', e.target.value)} placeholder={t('publishingRulesTab.timezonePlaceholder')} />
          </Field>
        </div>

        <Field label={t('publishingRulesTab.postsPerWeekLabel', { count: publishingForm.postsPerWeek })} hint={t('publishingRulesTab.postsPerWeekHint')}>
          <NativeSlider value={Number(publishingForm.postsPerWeek) || 3} onChange={(v) => update('postsPerWeek', v)} min={1} max={21} />
        </Field>

        <Field label={t('publishingRulesTab.preferredDaysLabel')}>
          <div className="cp-chip-toggle-row">
            {DAY_OPTIONS.map((day) => {
              const on = days.includes(day);
              return (
                <button
                  type="button"
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}
                >
                  {day.slice(0, 3).toUpperCase()}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label={t('publishingRulesTab.preferredTimesLabel')} hint={t('publishingRulesTab.preferredTimesHint')}>
          <input
            className="input form-input"
            value={publishingForm.preferredTimesText}
            onChange={(e) => update('preferredTimesText', e.target.value)}
            placeholder={t('publishingRulesTab.preferredTimesPlaceholder')}
          />
        </Field>

        <div className="settings-card-footer">
          <span />
          <button className="btn btn-primary" onClick={saveGlobal} disabled={savingGlobal}>
            <Icon name="check" size={13} /> {savingGlobal ? t('publishingRulesTab.saving') : t('publishingRulesTab.savePreferences')}
          </button>
        </div>
      </div>

      <div className="card ds-card-pad settings-card">
        <div className="settings-card-header">
          <h3 className="settings-card-title">{t('publishingRulesTab.perPlatformTitle')}</h3>
          <p className="settings-card-description">{t('publishingRulesTab.perPlatformDescription')}</p>
        </div>

        <div className="cp-platform-list">
          {PLATFORMS.map((platform) => (
            <PlatformRow
              key={platform}
              platform={platform}
              prefs={platformPrefs.find((p) => p.platform === platform)}
              setPlatformPrefs={setPlatformPrefs}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function PlatformRow({ platform, prefs, setPlatformPrefs }) {
  const { t } = useTranslation();
  const initial = prefs ?? {
    platform,
    enabled: false,
    autopilotEnabled: false,
    preferredFormats: [],
    excludedContentGoals: [],
    defaultTone: null,
    defaultVisualStyle: null,
    maxPostsPerDay: 1,
    maxPostsPerWeek: 5,
  };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setDraft(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(prefs)]);

  const update = (key, value) => {
    setDraft((prev) => {
      const next = { ...prev, [key]: value };
      scheduleSave(next);
      return next;
    });
  };

  function scheduleSave(next) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => save(next), 800);
  }

  async function save(next) {
    try {
      setSaving(true);
      const result = await api.profile.updatePlatformPreference({
        platform: next.platform,
        enabled: !!next.enabled,
        autopilotEnabled: !!next.autopilotEnabled,
        preferredFormats: next.preferredFormats || [],
        excludedContentGoals: next.excludedContentGoals || [],
        defaultTone: next.defaultTone || null,
        defaultVisualStyle: next.defaultVisualStyle || null,
        maxPostsPerDay: Number(next.maxPostsPerDay) || null,
        maxPostsPerWeek: Number(next.maxPostsPerWeek) || null,
      });
      const saved = result.platformPreference || next;
      setDraft(saved);
      setPlatformPrefs((prev) => {
        const filtered = prev.filter((p) => p.platform !== platform);
        return [...filtered, saved];
      });
      notify('publishing.platformSaved', { values: { platform: PLATFORM_LABELS[platform] } });
    } catch (err) {
      notify('publishing.platformSaveFailed', { error: err, values: { platform: PLATFORM_LABELS[platform] } });
    } finally {
      setSaving(false);
    }
  }

  const toggleFormat = (fmt) => {
    const set = new Set(draft.preferredFormats || []);
    if (set.has(fmt)) set.delete(fmt); else set.add(fmt);
    update('preferredFormats', Array.from(set));
  };
  const toggleGoal = (goal) => {
    const set = new Set(draft.excludedContentGoals || []);
    if (set.has(goal)) set.delete(goal); else set.add(goal);
    update('excludedContentGoals', Array.from(set));
  };

  return (
    <div className={'cp-platform-row ' + (draft.enabled ? '' : 'is-disabled')}>
      <div className="cp-platform-row-head">
        <div className="row" style={{ gap: 12, alignItems: 'center' }}>
          <PlatformIcon platform={platform} size={20} />
          <div>
            <div style={{ fontWeight: 600 }}>{PLATFORM_LABELS[platform]}</div>
            <div className="caption muted" style={{ fontSize: 12 }}>
              {draft.enabled ? (draft.autopilotEnabled ? t('publishingRulesTab.statusAutopilotOn') : t('publishingRulesTab.statusManual')) : t('publishingRulesTab.statusDisabled')}
              {saving && ` · ${t('publishingRulesTab.statusSaving')}`}
            </div>
          </div>
        </div>
        <Toggle checked={!!draft.enabled} onChange={(v) => update('enabled', v)} label={t('publishingRulesTab.enabledLabel')} />
      </div>

      {draft.enabled && (
        <div className="cp-platform-row-body">
          <div className="ds-setting-row" style={{ borderTop: 0, paddingTop: 0 }}>
            <div className="grow">
              <div style={{ fontWeight: 600 }}>{t('publishingRulesTab.autopilotRowLabel')}</div>
              <div className="caption muted" style={{ fontSize: 12 }}>{t('publishingRulesTab.autopilotRowDescription')}</div>
            </div>
            <Toggle checked={!!draft.autopilotEnabled} onChange={(v) => update('autopilotEnabled', v)} />
          </div>

          <div className="profile-form-grid">
            <Field label={t('publishingRulesTab.defaultToneLabel')}>
              <select className="input select form-input" value={draft.defaultTone || ''} onChange={(e) => update('defaultTone', e.target.value || null)}>
                <option value="">{t('publishingRulesTab.inheritFromBrand')}</option>
                {TONE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>
            <Field label={t('publishingRulesTab.defaultVisualStyleLabel')}>
              <select className="input select form-input" value={draft.defaultVisualStyle || ''} onChange={(e) => update('defaultVisualStyle', e.target.value || null)}>
                <option value="">{t('publishingRulesTab.inheritFromBrand')}</option>
                {VISUAL_STYLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label={t('publishingRulesTab.preferredFormatsLabel')}>
            <div className="cp-chip-toggle-row">
              {FORMAT_OPTIONS.map((fmt) => {
                const on = (draft.preferredFormats || []).includes(fmt.value);
                return (
                  <button
                    key={fmt.value}
                    type="button"
                    onClick={() => toggleFormat(fmt.value)}
                    className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}
                  >
                    {fmt.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field label={t('publishingRulesTab.excludedGoalsLabel')} hint={t('publishingRulesTab.excludedGoalsHint')}>
            <div className="cp-chip-toggle-row">
              {GOAL_OPTIONS.map((goal) => {
                const on = (draft.excludedContentGoals || []).includes(goal.value);
                return (
                  <button
                    key={goal.value}
                    type="button"
                    onClick={() => toggleGoal(goal.value)}
                    className={'chip cp-chip-toggle ' + (on ? 'chip-red' : '')}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="profile-form-grid">
            <Field label={t('publishingRulesTab.maxPostsPerDayLabel', { count: draft.maxPostsPerDay ?? 1 })}>
              <NativeSlider value={Number(draft.maxPostsPerDay) || 1} onChange={(v) => update('maxPostsPerDay', v)} min={1} max={20} />
            </Field>
            <Field label={t('publishingRulesTab.maxPostsPerWeekLabel', { count: draft.maxPostsPerWeek ?? 5 })}>
              <NativeSlider value={Number(draft.maxPostsPerWeek) || 5} onChange={(v) => update('maxPostsPerWeek', v)} min={1} max={100} />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}

export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!checked)}
      className={'switch ' + (checked ? 'on' : '')}
    />
  );
}

export function NativeSlider({ value, onChange, min = 0, max = 100, step = 1 }) {
  return (
    <input
      type="range"
      className="cp-slider"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange?.(Number(e.target.value))}
    />
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="form-field cp-field">
      <label>
        {label}
        {required && <span className="cp-required"> *</span>}
      </label>
      {children}
      {hint && <div className="helper">{hint}</div>}
    </div>
  );
}
